/**
 * Ortografia-specific event helpers.
 * 
 * Thin wrapper around the generic dispatchEvent function for Ortografia game events.
 * Ortografia has three event types:
 * - 'set_difficulty': Set the player's difficulty level
 * - 'submit_word': Submit a word for validation
 * - 'hint_scaffold': Request a scaffold hint showing a word outline
 * - 'view_solutions': Explicitly reveal solutions when available
 * 
 * Usage:
 * ```javascript
 * const { dispatchEvent, dispatchHintEvent } = useGameplaySession('ortografia', date);
 * const { setDifficulty, submitWord, requestScaffold } = useOrtografiaEvents(dispatchEvent, dispatchHintEvent);
 * 
 * await setDifficulty('B2');
 * const result = await submitWord('spiaggia');
 * if (result.success && result.result.valid) {
 *   // result.result contains: { valid, word, points, is_pangram, new_score, just_won }
 * }
 * ```
 */

import { useCallback } from 'react';

/**
 * Hook providing Ortografia-specific event helpers.
 * 
 * @param {function} dispatchEvent - The dispatchEvent function from useGameplaySession
 * @param {function} dispatchHintEvent - The dispatchHintEvent function from useGameplaySession
 * @returns {object} Ortografia event helpers
 * @returns {function} returns.setDifficulty - Set the player's difficulty level
 * @returns {function} returns.submitWord - Submit a word for validation
 * @returns {function} returns.requestScaffold - Request a scaffold hint
 * @returns {function} returns.viewSolutions - Explicitly reveal solutions
 */
export function useOrtografiaEvents(dispatchEvent, dispatchHintEvent) {
  /**
   * Set the player's difficulty level.
   * 
   * @param {string} difficulty - Difficulty level ('A1', 'A2', 'B1', 'B2', 'C1', 'C2')
   * @returns {Promise<{
   *   success: boolean,
   *   result?: { difficulty: string, threshold: number },
   *   state?: object,
   *   error?: string,
   *   message?: string
   * }>}
   * 
   * @example
   * const result = await setDifficulty('B2');
   * if (result.success) {
   *   // result.result.threshold contains the score needed to win
   * }
   */
  const setDifficulty = useCallback(async (difficulty) => {
    return dispatchEvent('set_difficulty', { difficulty });
  }, [dispatchEvent]);

  /**
   * Submit a word for validation.
   * 
   * @param {string} word - The word to submit (lowercase, may have accents)
   * @returns {Promise<{
   *   success: boolean,
   *   result?: {
   *     valid: boolean,
   *     reason?: 'TOO_SHORT' | 'NO_CENTER' | 'ALREADY_FOUND' | 'NOT_IN_LIST',
   *     word?: string,        // Accented form of the word
   *     points?: number,      // Points earned
   *     is_pangram?: boolean, // Whether word uses all 7 letters
   *     new_score?: number,   // New total score
   *     just_won?: boolean    // Whether this submission triggered a win
   *   },
   *   state?: object,
   *   error?: string, // Reserved for transport/session errors (not invalid words)
   *   message?: string
   * }>}
   * 
   * @example
   * const result = await submitWord('spiaggia');
   * if (result.success) {
   *   if (result.result.valid) {
   *     // Word accepted - show points earned
   *   } else {
   *     // Word rejected by game rules
   *     // result.result.reason: 'TOO_SHORT' | 'NO_CENTER' | 'ALREADY_FOUND' | 'NOT_IN_LIST'
   *   }
   * }
   */
  const submitWord = useCallback(async (word) => {
    return dispatchEvent('submit_word', { word });
  }, [dispatchEvent]);

  /**
   * Request a scaffold hint showing a word outline with some letters revealed.
   * Requires authentication and sufficient gettoncini balance.
   * This hint can be used multiple times (charged each time).
   * 
   * @param {function} onNotAuthenticated - Callback when user is not signed in
   * @returns {Promise<{
   *   success: boolean,
   *   hint_data?: {
   *     word_length: number,
   *     scaffold: string,              // e.g., "_ A _ _ O"
   *     revealed_positions: number[],  // e.g., [1, 4]
   *     revealed_letters: object       // e.g., { 1: "A", 4: "O" }
   *   },
   *   state?: object,
   *   error?: string,
   *   message?: string
   * }>}
   * 
   * @example
   * const result = await requestScaffold(() => showSignupModal());
   * if (result.success) {
   *   const { word_length, scaffold, revealed_letters } = result.hint_data;
   *   // Display scaffold hint to user
   * }
   */
  const requestScaffold = useCallback(async (onNotAuthenticated = null) => {
    return dispatchHintEvent('hint_scaffold', {}, onNotAuthenticated);
  }, [dispatchHintEvent]);

  const viewSolutions = useCallback(async () => {
    return dispatchEvent('view_solutions', {});
  }, [dispatchEvent]);

  return {
    setDifficulty,
    submitWord,
    requestScaffold,
    viewSolutions,
  };
}
